/**
 * Валюты: гибридная модель.
 *
 * - **Встроенные** (BUILTIN_CCY_CODES) — зашиты в коде, метаданные ниже,
 *   сид upsert'ит их в `reference.currencies` при каждом запуске.
 * - **Пользовательские** — добавляются через UI (/admin/rates → «+ Валюта»),
 *   живут только в БД. Тип `CcyCode` поэтому `string`, а не литеральный union.
 * - Фронт после загрузки `GET /ref/currencies` вызывает `applyCurrencyMeta()` —
 *   метаданные из БД (символ/позиция/scale/порядок) попадают в реестр и
 *   `fmtAmt`/сортировки начинают работать для новых валют без рестарта.
 * - Для неизвестного кода fmtAmt деградирует мягко: символ = сам код, scale 2.
 */

export const BUILTIN_CCY_CODES = ['USD', 'EUR', 'GBP', 'USDT', 'RUB', 'CNY', 'AED'] as const;
export type BuiltinCcyCode = (typeof BUILTIN_CCY_CODES)[number];

/** Код валюты. Встроенные + добавленные через UI (валидация — CcyCodeSchema + БД). */
export type CcyCode = string;

/** @deprecated статичный список только встроенных валют; для UI-списков бери
 *  валюты из API (`useCurrencies`). Остаётся для сидера и fallback'ов. */
export const CCY_CODES = BUILTIN_CCY_CODES;

// satisfies гарантирует полноту по встроенным валютам (typecheck поймает пропуск),
// Record<string, ...> позволяет дописывать пользовательские в рантайме.
export const CCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  USDT: '$',
  RUB: '₽',
  CNY: '¥',
  AED: 'د.إ',
} satisfies Record<BuiltinCcyCode, string>;

export const CCY_POSITION: Record<string, 'before' | 'after'> = {
  USD: 'before',
  EUR: 'before',
  GBP: 'before',
  USDT: 'before',
  RUB: 'after',
  CNY: 'after',
  AED: 'after',
} satisfies Record<BuiltinCcyCode, 'before' | 'after'>;

export const CCY_SCALE: Record<string, number> = {
  USD: 2,
  EUR: 2,
  GBP: 2,
  USDT: 8,
  RUB: 2,
  CNY: 2,
  AED: 2,
} satisfies Record<BuiltinCcyCode, number>;

export const CCY_SORT_ORDER: Record<string, number> = {
  USD: 1,
  EUR: 2,
  GBP: 3,
  USDT: 4,
  RUB: 5,
  CNY: 6,
  AED: 7,
} satisfies Record<BuiltinCcyCode, number>;

export interface CurrencyMeta {
  code: string;
  symbol: string;
  scale: number;
  position: 'before' | 'after';
  sortOrder: number;
}

/**
 * Влить метаданные валют из БД в рантайм-реестр. Вызывается фронтом после
 * загрузки `GET /ref/currencies` (см. apps/web useCurrencies) — после этого
 * fmtAmt и сортировки знают о пользовательских валютах. Идемпотентно.
 */
export function applyCurrencyMeta(list: CurrencyMeta[]): void {
  for (const c of list) {
    CCY_SYMBOLS[c.code] = c.symbol;
    CCY_POSITION[c.code] = c.position;
    CCY_SCALE[c.code] = c.scale;
    CCY_SORT_ORDER[c.code] = c.sortOrder;
  }
}

/**
 * Символы валют для regex-альтернаций (visual rule builder telegram-listener,
 * тип переменной `ccySymbol`). Встроенные символы + распространённые в
 * банковских/P2P-уведомлениях, которых нет среди встроенных валют.
 *
 * Статичный снимок на момент загрузки модуля — regex-паттерн правила
 * замораживается при создании правила, поэтому пользовательские валюты из БД
 * (applyCurrencyMeta) сюда осознанно не попадают.
 */
export const KNOWN_CCY_SYMBOLS: readonly string[] = [
  ...new Set([
    ...BUILTIN_CCY_CODES.map((code) => CCY_SYMBOLS[code] as string),
    // Распространённые символы вне списка встроенных валют:
    '₮', // USDT (Tether)
    '＄', // fullwidth dollar — встречается в бот-уведомлениях
    '￥', // fullwidth yen — так бот пишет остаток CNY («CNY 0.00 ￥»)
    '₴', // UAH
    '₸', // KZT
    '₺', // TRY
  ]),
];

export function isCcyCode(value: unknown): value is CcyCode {
  return typeof value === 'string' && /^[A-Z0-9]{2,12}$/.test(value);
}
